// Amadeus seat handling — the provider's complete ownership of seat data.
// Three responsibilities live here:
//
//   1. Ingest  — build_deck_seatmap parses the raw Amadeus SeatMap envelope
//                into the canonical NormalizedDeckSeatMap shape the UI
//                consumes. Every Amadeus-specific table (characteristicsCodes,
//                notActuallySeats, isBlackListedByCarrier, exitRowsX,
//                coordinates.{x,y}, seatAvailabilityStatus enum) is resolved
//                here — nothing Amadeus-shaped leaks past this function.
//
//   2. Guard   — validate_deck_configuration / validate_traveler_pricing
//                gate the raw envelope before the grid builder runs.
//                Consumers call them against the raw envelope; build_deck_
//                seatmap also re-runs deck-configuration validation and
//                returns null for malformed decks.
//
//   3. Egress  — apply_seats_to_raw writes the canonical Selections into a
//                copy of the raw offer and fetch_seats_applied reprices it
//                upstream. The Amadeus reprice computes grandTotal / taxes;
//                we only set fareDetailsBySegment[].additionalServices.
//                chargeableSeatNumber so the pricing engine bills correctly.
//
// See providers/types.js for NormalizedDeckSeatMap / SeatBlock / SeatSelection
// / Selections typedefs.

import { service_handler } from '@/services';
import { apply_markup_to_number } from '@liveflight/utilities/price';
import {
  build_legend_hints,
  deck_covers_all_passengers,
  seat_restriction_reason,
} from '@liveflight/utilities/seat-map';
import { currency_symbol } from '@resources';
import { RYANAIR_CARRIER_CODES } from '@liveflight/resources/carriers';
import { is_infant } from '@liveflight/utilities/travelers';
import {
  isBlackListedByCarrier,
  notActuallySeats,
  premiumSeatCharacteristics,
  restrictedTravelerTypesByCharacteristic,
  SEAT_SUITABLE_FOR_INFANT,
  userKnowledgeSeatCharacteristics,
} from './resources/seat-map';
import { normalize } from './normalize';
import {
  build_passenger_summary,
  build_deck_summary,
  build_restricted_for,
  index_infant_holders,
  index_traveler_types,
  is_partially_available,
  merge_dictionaries,
} from '../base/helpers';
import { extract_dictionaries } from './dictionaries';

const notActuallySeatsCodes = new Set(Object.keys(notActuallySeats));

// ─── Private helpers ─────────────────────────────────────────────────────

const parse_seat_number = (seat_number) => ({
  row: seat_number.substring(0, seat_number.length - 1),
  column: seat_number[seat_number.length - 1],
});

const is_actual_seat = (seat) => {
  if (!seat) return false;
  const codes = seat.characteristicsCodes || [];
  if (!codes.length) return true;
  return !codes.some((code) => notActuallySeatsCodes.has(code));
};

const is_carrier_blacklisted = (seat, carrier_code) => {
  const checker = isBlackListedByCarrier[carrier_code];
  return !!(checker && checker(seat.characteristicsCodes));
};

// Pivot Amadeus characteristic codes into the canonical `restrictedFor` map
// that seat_restriction_reason reads — keyed by the traveler_type tokens
// each code bars.
const build_seat_restrictions = (characteristicsCodes) =>
  build_restricted_for(
    characteristicsCodes.flatMap(
      (code) => restrictedTravelerTypesByCharacteristic[code] || []
    )
  );

// ─── Validators ──────────────────────────────────────────────────────────
//
// Both read the raw Amadeus envelope (seat.coordinates.y, travelerPricing[]
// on raw seats, deckConfiguration.width). They never see the normalized
// shape — they run before build_deck_seatmap.

/**
 * ITSM-513: verify every non-infant traveler has pricing data in at
 * least one seat across the entire seatmap response.
 *
 * @param {Object} flight_offer
 * @param {Array}  seat_map_data
 * @returns {boolean}
 */
export const validate_traveler_pricing = (flight_offer, seat_map_data) => {
  const { travelerPricings = [] } = flight_offer || {};
  const traveler_ids = travelerPricings
    .filter((t) => !is_infant(t))
    .map((t) => t.travelerId);

  const all_seats = (seat_map_data || []).flatMap((segment) =>
    (segment.decks || []).flatMap((deck) => deck.seats || [])
  );

  if (!traveler_ids.length || !all_seats.length) return false;

  return traveler_ids.every((id) =>
    all_seats.some((seat) =>
      (seat.travelerPricing || []).some((p) => p.travelerId === id)
    )
  );
};

/**
 * ITSM-513: verify deckConfiguration.width is consistent with the actual
 * Y-coordinates of the returned seats. Rejects overflow (seats beyond
 * grid), allows underflow (grid wider than seats, e.g. narrow tail).
 * Exported for the dedicated test block; build_deck_seatmap runs this
 * internally and returns null on failure unless `allowMalformed` is set.
 *
 * @param {Object} deck_configuration
 * @param {Array}  seats
 * @returns {boolean}
 */
export const validate_deck_configuration = (deck_configuration, seats) => {
  const { width } = deck_configuration || {};
  if (!width || width <= 0) return false;
  if (!seats || !seats.length) return true;

  const max_y = seats.reduce((max, seat) => {
    const { coordinates: { y } = {} } = seat || {};
    return y !== undefined && !Number.isNaN(y) && y > max ? y : max;
  }, -1);

  if (max_y < 0) return false;

  return max_y <= width - 1;
};

// ─── Ingest: raw Amadeus deck → NormalizedDeckSeatMap ────────────────────
//
// Single-pass builder. Walks the raw Amadeus deck, applies every blocking
// policy (actual-seat, carrier-blacklist, missing-price) producing the
// normalized availability enum on each block, and aggregates per-traveler
// counters + deck-level summary in the same loop.
//
// `availableSeatsCount[tid]` is post-policy; `availableSeatsRemoved[tid]`
// is what the policy downgraded. Together they reconstruct raw
// availability. Consumers never need to know which provider produced
// the counts.

/**
 * @param {Object} flight_offer
 * @param {Object} deck             raw Amadeus deck envelope
 * @param {string} carrier_code
 * @param {Object} [dictionary={}]
 * @param {{ allowMalformed?: boolean }} [options={}]
 * @returns {Object|null} NormalizedDeckSeatMap, or null when the deck is malformed
 */
export const build_deck_seatmap = (
  flight_offer,
  deck,
  carrier_code,
  dictionary = {},
  options = {}
) => {
  if (!deck) return null;

  const { allowMalformed = false } = options;
  if (
    !allowMalformed &&
    !validate_deck_configuration(deck.deckConfiguration, deck.seats)
  ) {
    return null;
  }

  const aircraftMap = {};
  const columns = {};
  const rows = {};
  const priceMap = {};
  const availableSeatsRemoved = {};
  const availableSeatsCount = {};
  const passengerMinPrice = {};
  const passengerMaxPrice = {};
  const passengerHasInfantSeat = {};
  const lastSeatPerRow = {};
  const travelerTypes = index_traveler_types(flight_offer);
  const infantHolders = index_infant_holders(flight_offer);

  const { deckConfiguration = {}, seats = [], facilities = [] } = deck;
  const { width = 0, exitRowsX: exitRows = [] } = deckConfiguration;
  let hasSeatsSuitableForInfant = false;
  let deckHasPremiumSeats = false;
  let deckTotalSeats = 0;
  let deckAvailableSeats = 0;
  let deckHasFreeSeats = false;
  let deckHasChargeableSeats = false;
  let deckHasOccupiedSeats = false;
  let deckHasBlockedSeats = false;
  let deckMinPrice = null;
  let deckMaxPrice = null;
  let deckPriceCurrency = null;
  let deckPriceCurrencySymbol = null;

  for (let col = 0; col < width; col += 1) {
    columns[col] = { name: '', isHallway: true };
  }

  seats.forEach((seat) => {
    const { coordinates = {} } = seat || {};
    const { x, y } = coordinates;
    if (!aircraftMap[x]) {
      aircraftMap[x] = { blocks: {} };
    }
    if (
      Number.isNaN(y) ||
      Number.isNaN(x) ||
      x === undefined ||
      y === undefined
    ) {
      return;
    }

    const { row: seatRow, column: seatColumn } = parse_seat_number(seat.number);
    const actualSeat = is_actual_seat(seat);
    const carrierBlackListed = is_carrier_blacklisted(seat, carrier_code);
    const characteristicsCodes = seat.characteristicsCodes || [];
    const isSuitableForInfantSeat = characteristicsCodes.includes(
      SEAT_SUITABLE_FOR_INFANT
    );
    const isPremiumSeat = characteristicsCodes.some(
      (ch) => premiumSeatCharacteristics[ch]
    );
    if (isPremiumSeat) deckHasPremiumSeats = true;
    const displayCharacteristics = characteristicsCodes
      .filter((ch) => userKnowledgeSeatCharacteristics[ch])
      .map((ch) => ({ code: ch, label: userKnowledgeSeatCharacteristics[ch] }));
    const restrictedFor = build_seat_restrictions(characteristicsCodes);

    let rawAnyAvailable = false;
    let postBlockingAnyAvailable = false;

    const traveler_pricing_list = Array.isArray(seat.travelerPricing)
      ? seat.travelerPricing
      : [];

    // One verdict per traveler, resolved once so the AVAILABLE downgrade
    // below and the popover copy can't disagree.
    const restrictionMessages = {};
    traveler_pricing_list.forEach(({ travelerId }) => {
      restrictionMessages[travelerId] = seat_restriction_reason(
        { restrictedFor, isSuitableForInfant: isSuitableForInfantSeat },
        travelerTypes[travelerId],
        !!infantHolders[travelerId]
      );
    });

    const processedPricing = traveler_pricing_list.map((pricing) => {
      const hasPrice = !!pricing.price;
      // A negative total is never billable; block the seat like a missing
      // price rather than presenting it as a discount. '' is not negative.
      const priceTotal = hasPrice ? pricing.price.total : undefined;
      const priceIsNegative =
        priceTotal !== undefined && priceTotal !== '' && Number(priceTotal) < 0;
      let seatAvailabilityStatus = (
        pricing.seatAvailabilityStatus || 'BLOCKED'
      ).toUpperCase();
      let blackListed = false;

      if (seatAvailabilityStatus === 'AVAILABLE') {
        rawAnyAvailable = true;
      } else {
        if (seatAvailabilityStatus === 'OCCUPIED') deckHasOccupiedSeats = true;
        if (seatAvailabilityStatus === 'BLOCKED') deckHasBlockedSeats = true;
      }

      if (seatAvailabilityStatus === 'AVAILABLE') {
        // Barred to their type, or kept infant-free while they carry a lap
        // infant: blocked for them alone. The row is already per-traveler, so
        // counters, min/max and pricingByTraveler follow without further work.
        const restricted = !!restrictionMessages[pricing.travelerId];
        if (
          !actualSeat ||
          !hasPrice ||
          priceIsNegative ||
          carrierBlackListed ||
          restricted
        ) {
          seatAvailabilityStatus = 'BLOCKED';
          blackListed = true;
          deckHasBlockedSeats = true;
          availableSeatsRemoved[pricing.travelerId] =
            (availableSeatsRemoved[pricing.travelerId] || 0) + 1;
        }
      }

      if (seatAvailabilityStatus === 'AVAILABLE') {
        postBlockingAnyAvailable = true;
        availableSeatsCount[pricing.travelerId] =
          (availableSeatsCount[pricing.travelerId] || 0) + 1;
        if (isSuitableForInfantSeat) {
          passengerHasInfantSeat[pricing.travelerId] = true;
        }
      }

      if (
        !hasPrice ||
        !pricing.price.total ||
        !flight_offer ||
        !flight_offer.travelerPricings
      ) {
        return {
          ...pricing,
          seatAvailabilityStatus,
          blackListed,
        };
      }

      const { total } = pricing.price;
      let grandTotal = priceMap[total];
      if (!grandTotal) {
        grandTotal = apply_markup_to_number(flight_offer, Number(total));
        priceMap[total] = grandTotal;
      }

      const { currency: pricing_currency = '' } = pricing.price || {};
      const priceCurrencySymbol = currency_symbol(pricing_currency);

      if (!deckPriceCurrency && pricing_currency) {
        deckPriceCurrency = pricing_currency;
        deckPriceCurrencySymbol = priceCurrencySymbol;
      }

      if (seatAvailabilityStatus === 'AVAILABLE') {
        // `total === ''` also yields Number('') === 0 — distinguish
        // empty from real "0.00" to avoid mis-classifying as free.
        const totalNumber = total === '' ? NaN : Number(total);
        const grandTotalNumber = Number(grandTotal);
        if (Number.isFinite(totalNumber)) {
          if (totalNumber === 0) {
            deckHasFreeSeats = true;
          } else {
            deckHasChargeableSeats = true;
          }
        }
        if (deckMinPrice === null || grandTotalNumber < deckMinPrice) {
          deckMinPrice = grandTotalNumber;
        }
        if (deckMaxPrice === null || grandTotalNumber > deckMaxPrice) {
          deckMaxPrice = grandTotalNumber;
        }
        const currentMin = passengerMinPrice[pricing.travelerId];
        if (!currentMin || grandTotalNumber < Number(currentMin.total)) {
          passengerMinPrice[pricing.travelerId] = {
            total: String(grandTotal),
            currency: pricing.price.currency,
            currencySymbol: priceCurrencySymbol,
          };
        }
        const currentMax = passengerMaxPrice[pricing.travelerId];
        if (!currentMax || grandTotalNumber > Number(currentMax.total)) {
          passengerMaxPrice[pricing.travelerId] = {
            total: String(grandTotal),
            currency: pricing.price.currency,
            currencySymbol: priceCurrencySymbol,
          };
        }
      }

      return {
        ...pricing,
        seatAvailabilityStatus,
        blackListed,
        price: {
          ...pricing.price,
          grandTotal,
          currencySymbol: priceCurrencySymbol,
        },
      };
    });

    deckTotalSeats += 1;
    if (postBlockingAnyAvailable) {
      deckAvailableSeats += 1;
    }

    const pricingByTraveler = {};
    processedPricing.forEach((p) => {
      const priceBlock =
        p.price && p.price.total !== undefined
          ? {
              total: String(p.price.total),
              grandTotal:
                p.price.grandTotal !== undefined
                  ? String(p.price.grandTotal)
                  : String(p.price.total),
              currency: p.price.currency,
              currencySymbol: p.price.currencySymbol,
            }
          : null;
      pricingByTraveler[p.travelerId] = {
        availability: String(p.seatAvailabilityStatus).toLowerCase(),
        blackListed: p.blackListed,
        price: priceBlock,
        restrictionMessage: restrictionMessages[p.travelerId],
      };
    });

    const block = {
      ...seat,
      isPartiallyAvailable: is_partially_available(pricingByTraveler),
      isSuitableForInfant: isSuitableForInfantSeat,
      isPremium: isPremiumSeat,
      displayCharacteristics,
      restrictedFor,
      pricingByTraveler,
      travelerPricing: processedPricing,
      row: seatRow,
      column: seatColumn,
      type: 'seat',
    };
    aircraftMap[x].blocks[y] = block;

    const prevY = lastSeatPerRow[x];
    if (prevY !== undefined) {
      const seatToTheLeft = aircraftMap[x].blocks[prevY];
      block.left = {
        number: seatToTheLeft.number,
        isBlocked: !seatToTheLeft._postBlockingAvailable,
      };
      seatToTheLeft.right = {
        number: seat.number,
        isBlocked: !rawAnyAvailable,
      };
    }
    lastSeatPerRow[x] = y;

    block._postBlockingAvailable = postBlockingAnyAvailable;

    if (isSuitableForInfantSeat && rawAnyAvailable) {
      hasSeatsSuitableForInfant = true;
    }

    columns[y] = { name: seatColumn, isHallway: false };
    rows[x] = Number(seatRow);
  });

  Object.keys(aircraftMap).forEach((row) => {
    const blocks = aircraftMap[row].blocks || {};
    Object.keys(blocks).forEach((col) => {
      delete blocks[col]._postBlockingAvailable;
    });
  });

  const facilitiesDictionary = dictionary.facilities || {};
  facilities.forEach((facility, i) => {
    const { coordinates } = facility;
    const { x, y } = coordinates;
    if (!aircraftMap[x]) {
      aircraftMap[x] = { blocks: {} };
    }
    if (Number.isNaN(y) || Number.isNaN(x)) {
      return;
    }
    aircraftMap[x].blocks[y] = {
      ...facility,
      facilityId: [
        facility.position,
        facility.column,
        facility.row,
        facility.code,
        i,
      ]
        .filter((f) => f !== undefined && f !== null && f !== '')
        .join('-'),
      description: facilitiesDictionary[facility.code],
      type: 'facility',
    };
  });

  exitRows.forEach((exit) => {
    if (!aircraftMap[exit]) {
      aircraftMap[exit] = { exits: {} };
    }
    aircraftMap[exit].exits = {
      0: { type: 'exit', direction: 'left' },
      [width - 1]: { type: 'exit', direction: 'right' },
    };
    for (let col = 1; col < width - 1; col += 1) {
      aircraftMap[exit].exits[col] = { type: 'empty' };
    }
  });

  Object.keys(aircraftMap).forEach((row) => {
    const blocks = (aircraftMap[row] || {}).blocks || {};
    for (let col = 0; col < width; col += 1) {
      if (!blocks[col]) {
        blocks[col] = { type: 'empty' };
      }
    }
    aircraftMap[row].blocks = blocks;
  });

  const columnKeys = Object.keys(columns);
  const adjustedColumns = { ...columns };
  if (columnKeys.length > 2) {
    for (let col = 1; col < columnKeys.length - 1; col += 1) {
      const column = columns[col];
      if (column.isHallway) {
        const leftIsHallway = columns[col - 1] && columns[col - 1].isHallway;
        const rightIsHallway = columns[col + 1] && columns[col + 1].isHallway;
        adjustedColumns[col] = {
          name: column.name,
          isHallway:
            (leftIsHallway && rightIsHallway) ||
            (!leftIsHallway && !rightIsHallway),
        };
      }
    }
  }

  const travelerIds = new Set([
    ...Object.keys(availableSeatsCount),
    ...Object.keys(availableSeatsRemoved),
    ...Object.keys(passengerMinPrice),
    ...Object.keys(passengerMaxPrice),
    ...Object.keys(passengerHasInfantSeat),
  ]);
  const passengerSummary = build_passenger_summary({
    travelerIds,
    availableSeatsCount,
    availableSeatsRemoved,
    passengerHasInfantSeat,
    passengerMinPrice,
    passengerMaxPrice,
  });

  const deckSummary = build_deck_summary({
    totalSeats: deckTotalSeats,
    availableSeats: deckAvailableSeats,
    hasFreeSeats: deckHasFreeSeats,
    hasChargeableSeats: deckHasChargeableSeats,
    minPrice: deckMinPrice,
    maxPrice: deckMaxPrice,
    currency: deckPriceCurrency,
    currencySymbol: deckPriceCurrencySymbol,
  });

  const legendHints = build_legend_hints({
    hasFreeSeats: deckHasFreeSeats,
    hasChargeableSeats: deckHasChargeableSeats,
    hasOccupiedSeats: deckHasOccupiedSeats,
    hasBlockedSeats: deckHasBlockedSeats,
    hasInfantSuitableSeats: hasSeatsSuitableForInfant,
    hasPremiumSeats: deckHasPremiumSeats,
    hasExitRows: exitRows.length > 0,
    facilities,
    facilityDescriptions: facilitiesDictionary,
  });

  return {
    hasSeatsSuitableForInfant,
    aircraftMap,
    availableSeatsRemoved,
    availableSeatsCount,
    passengerSummary,
    deckSummary,
    columns: adjustedColumns,
    rows,
    currencyCode: deckPriceCurrency || '',
    currencySymbol: deckPriceCurrencySymbol || '',
    legendHints,
  };
};

// ─── Ingest: full seatmap response → mappedDecks ─────────────────────────

/**
 * Walk the Amadeus seatmap envelope (`data[].{segmentId, carrierCode,
 * decks[]}`) and emit the per-segment, per-deck structure the UI
 * consumes. Every per-deck gate is evaluated from the values
 * build_deck_seatmap already emits — nothing is recomputed here.
 *
 * @param {Object} flight_offer
 * @param {Object} seatmaps
 * @param {{ areInfantsPresent?: boolean, allowMalformed?: boolean }} [options={}]
 * @returns {Object} per-segment mapped-decks map
 */
export const build_mapped_decks = (flight_offer, seatmaps, options = {}) => {
  if (!seatmaps || !flight_offer) return {};
  const { areInfantsPresent = false, allowMalformed = false } = options;
  const dictionary = seatmaps.dictionaries || {};
  const data = seatmaps.data || [];
  const mapped = {};

  const all_segments = (flight_offer.itineraries || []).flatMap(
    (itin) => itin.segments || []
  );

  const consumed_data_indexes = {};
  const seen_segment_ids = {};

  all_segments.forEach((segment) => {
    let segment_seats_data = segment;
    const segment_decks = { 0: { disabled: true } };
    const idx = data.findIndex(
      (e, i) => e.segmentId === segment.id && !consumed_data_indexes[i]
    );
    if (idx >= 0) {
      consumed_data_indexes[idx] = true;
      segment_seats_data = data[idx];
      segment_seats_data.decks.forEach((deck, i) => {
        const mapped_deck = build_deck_seatmap(
          flight_offer,
          deck,
          segment_seats_data.carrierCode,
          dictionary,
          { allowMalformed }
        );
        if (!mapped_deck) {
          segment_decks[i] = { disabled: true };
          return;
        }
        const noInfantSeats =
          areInfantsPresent && !mapped_deck.hasSeatsSuitableForInfant;
        segment_decks[i] = {
          ...mapped_deck,
          disabled:
            !deck_covers_all_passengers(flight_offer, mapped_deck) ||
            noInfantSeats,
        };
      });
    }
    const raw_segment_id =
      segment_seats_data.segmentId || segment_seats_data.id;
    let mapped_key = raw_segment_id;
    if (seen_segment_ids[raw_segment_id]) {
      mapped_key = `${raw_segment_id}#${seen_segment_ids[raw_segment_id]}`;
    }
    seen_segment_ids[raw_segment_id] =
      (seen_segment_ids[raw_segment_id] || 0) + 1;
    mapped[mapped_key] = {
      decks: segment_decks,
      segment: {
        ...segment,
        ...segment_seats_data,
        segmentId: raw_segment_id,
        decks: segment_seats_data.decks || [{ disabled: true }],
        disabled: Object.values(segment_decks).every((d) => d.disabled),
      },
    };
  });

  return mapped;
};

// ─── Egress: Selections → repriced offer ─────────────────────────────────

/**
 * Pure. Produce an Amadeus-shape copy of the raw offer with
 * chargeableSeatNumber set/cleared on each traveler's
 * fareDetailsBySegment[] according to the Selections map. Does NOT
 * alter pricing — the upstream reprice owns the post-seat totals.
 *
 * @param {Object} raw_offer
 * @param {Object} selections Selections map (segmentId → travelerId → seat)
 * @returns {Object}
 */
export const apply_seats_to_raw = (raw_offer, selections) => {
  const safe = selections || {};
  return {
    ...raw_offer,
    travelerPricings: (raw_offer.travelerPricings || []).map((pricing) => ({
      ...pricing,
      fareDetailsBySegment: (pricing.fareDetailsBySegment || []).map(
        (fare_details) => {
          const { segmentId } = fare_details;
          const seats_for_segment = safe[segmentId];
          if (!seats_for_segment) {
            return fare_details;
          }
          const { travelerId } = pricing;
          const seat = seats_for_segment[travelerId];
          if (!seat) {
            const removed = { ...fare_details };
            if (removed.additionalServices) {
              const additional = { ...removed.additionalServices };
              delete additional.chargeableSeatNumber;
              if (Object.keys(additional).length === 0) {
                delete removed.additionalServices;
              } else {
                removed.additionalServices = additional;
              }
            }
            return removed;
          }
          const seat_number =
            typeof seat === 'string' ? seat : seat && seat.number;
          if (!seat_number) {
            return fare_details;
          }
          return {
            ...fare_details,
            additionalServices: {
              ...(fare_details.additionalServices || {}),
              chargeableSeatNumber: seat_number,
            },
          };
        }
      ),
    })),
  };
};

// Non-Basic Ryanair fares need a manual seat pick for every passenger.
// Basic fares (FRValue / BASIC) auto-allocate, so the pick stays optional.
const RYANAIR_BASIC_FARES = ['FRValue', 'BASIC'];

/**
 * @param {Object} flight_offer
 * @returns {boolean} true when the booking can't advance without an
 *                    explicit seat pick for every non-infant traveler
 */
export const seats_are_mandatory = (flight_offer) => {
  const { itineraries = [], travelerPricings = [] } = flight_offer || {};
  const has_ryanair_segment = itineraries.some((itinerary) => {
    const { segments = [] } = itinerary || {};
    return segments.some((segment) => {
      const { carrier = {} } = segment || {};
      return RYANAIR_CARRIER_CODES.includes(carrier.code || '');
    });
  });
  if (!has_ryanair_segment) return false;
  return travelerPricings.some((pricing) => {
    const { fareDetailsBySegment = [] } = pricing || {};
    return fareDetailsBySegment.some((fare) => {
      const { brandedFare = '' } = fare || {};
      return !brandedFare || !RYANAIR_BASIC_FARES.includes(brandedFare);
    });
  });
};

/**
 * Apply selections + reprice. The raw envelope never leaves this function.
 *
 * @param {Provider} provider
 * @param {Object} flight_offer
 * @param {Object} selections
 * @returns {Promise<Object>} repriced normalized FlightOffer
 * @throws {Error} when the reprice response carries no flightOffer
 */
export const fetch_seats_applied = (provider, flight_offer, selections) => {
  const raw_with_seats = apply_seats_to_raw(flight_offer._raw, selections);
  const body = {
    priceFlightOffersBody: {
      data: {
        type: 'flight-offers-pricing',
        flightOffers: [raw_with_seats],
      },
    },
  };
  return service_handler('flightOffersPrice', body).then((response) => {
    const { data: envelope = {} } = response || {};
    const { data: priced_data = {} } = envelope;
    const { flightOffers = [] } = priced_data;
    merge_dictionaries(provider, extract_dictionaries(response));
    const priced = flightOffers[0] || null;
    if (!priced) {
      throw new Error(
        '[AmadeusProvider] fetch_seats_applied: missing flightOffers[0] in reprice response'
      );
    }
    return normalize(provider, priced);
  });
};
